'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { FlatQuotationLine } from '@ravago/shared/page-data/models/elements';

import { CombinedResolution, compareQuoteLines } from '../utils/quote-pricing/combined-pricing.utils';

import { useDebouncedCommit } from './use-debounced-commit';

const DEBOUNCE_MS = 500;

/** Resolves the committed quantity, optionally honouring the quote line the user picked explicitly. */
export type QuantityResolver = (quantity: number, preferredQuoteLineId: string | undefined) => CombinedResolution;

interface Params {
   quoteLines: FlatQuotationLine[];
   /** Initial input used when there is no quote line to default to. */
   fallbackInput?: string;
   resolve: QuantityResolver;
}

/** Default quote line: lowest unit price, then packaging and quote name for deterministic tie-breaks. */
export const getDefaultQuoteLine = (quoteLines: FlatQuotationLine[]): FlatQuotationLine | undefined => {
   if (quoteLines.length === 0) return undefined;
   return [...quoteLines].sort(compareQuoteLines)[0];
};

/**
 * Owns the shared Amount input: its resolved value is the single source of truth for what the
 * summary and Add to cart reflect. Typing resolves through a debounce; clicking a line or
 * suggestion (`selectQuantity`) commits immediately.
 *
 * Quantity resolution itself is injected through `resolve`, so callers decide which price sources
 * take part (quotes only, or quotes combined with another source).
 */
export const usePricingQuantityState = ({ quoteLines, fallbackInput = '', resolve }: Params) => {
   const defaultLine = useMemo(() => getDefaultQuoteLine(quoteLines), [quoteLines]);
   const [preferredQuoteLineId, setPreferredQuoteLineId] = useState<string | undefined>(defaultLine?.lineId);

   useEffect(() => {
      setPreferredQuoteLineId(defaultLine?.lineId);
   }, [defaultLine?.lineId]);

   const { value: inputValue, committedValue, hasSettled, change: onInputChange, commitNow } = useDebouncedCommit(
      defaultLine ? defaultLine.lineQuantityAmount.toString() : fallbackInput,
      DEBOUNCE_MS
   );

   const parsedQuantity = useMemo(() => {
      const parsed = Number.parseFloat(committedValue);
      return Number.isNaN(parsed) ? 0 : parsed;
   }, [committedValue]);

   const resolution = useMemo<CombinedResolution>(() => resolve(parsedQuantity, preferredQuoteLineId), [resolve, parsedQuantity, preferredQuoteLineId]);

   // The explicit pick only applies while the input still resolves to a quote.
   useEffect(() => {
      if (resolution.kind !== 'quote' && preferredQuoteLineId !== undefined) {
         setPreferredQuoteLineId(undefined);
      }
   }, [resolution, preferredQuoteLineId]);

   const selectQuantity = useCallback(
      (quantity: number, quoteLineId?: string) => {
         if (quoteLineId) setPreferredQuoteLineId(quoteLineId);
         commitNow(quantity.toString());
      },
      [commitNow]
   );

   return {
      inputValue,
      onInputChange,
      hasSettled,
      resolution,
      selectQuantity,
      defaultLine
   };
};
